import { useMemo } from 'react'
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import camelcaseKeys from 'camelcase-keys'
import backend from '@api/backend'
import {
  notifyApiError,
  notifySuccess,
} from '@common/utils/notify'
import { emptyToNull } from '@common/utils/string'
import Account from '@data/accounts/model/account'
import { useAccountStore } from '@store/account.store'

export const ACCOUNTS_QUERY_KEY = ['accounts'] as const

const EMPTY_ACCOUNTS: Account[] = []

interface CurrentAccountResponse {
  id: number
  active: boolean
  account_name: string
  address_line_1: string
  address_line_2: string | null
  town: string | null
  postcode: string | null
  county: string | null
  country: string
  phone_no: string | null
  account_alias: string | null
  ex_summary_enabled: boolean
  report_30_enabled?: boolean
  sustainability_enabled?: boolean
  esg_enabled?: boolean
}

interface AccountPayload {
  id: number
  active: boolean
  accountName: string
  addressLine1: string
  addressLine2: string | null
  town: string | null
  postcode: string | null
  county: string | null
  country: string
  phoneNo?: string | null
  accountAlias: string | null
  exSummaryEnabled: boolean
  report30Enabled?: boolean
  sustainabilityEnabled?: boolean
  esgEnabled?: boolean
  lastComment?: Account['lastComment']
}

type AccountApiResponse = CurrentAccountResponse | AccountPayload

interface AccountCreateRequest {
  active: boolean
  accountName: string
  addressLine1: string
  addressLine2: string | null
  town: string | null
  postcode: string | null
  county: string | null
  country: string
  phoneNo: string | null
  accountAlias: string | null
}

interface AccountEditRequest {
  active?: boolean | null
  accountName?: string | null
  addressLine1?: string | null
  addressLine2?: string | null
  town?: string | null
  postcode?: string | null
  county?: string | null
  country?: string
  phoneNo?: string | null
  accountAlias?: string | null
  exSummaryEnabled?: boolean | null
}

export const normalizeAccountPayload = (raw: AccountApiResponse): AccountPayload =>
  camelcaseKeys(raw, { deep: true }) as AccountPayload

export const mapAccountResponse = (raw: AccountApiResponse): Account => {
  const account = normalizeAccountPayload(raw)

  return {
    id: String(account.id),
    active: account.active,
    accountName: account.accountName,
    addressLine1: account.addressLine1,
    addressLine2: account.addressLine2,
    town: account.town,
    postcode: account.postcode,
    county: account.county,
    country: account.country,
    phoneNo: account.phoneNo ?? null,
    accountAlias: account.accountAlias,
    report30Enabled: account.report30Enabled,
    sustainabilityEnabled: account.sustainabilityEnabled,
    exSummaryEnabled: account.exSummaryEnabled,
    esgEnabled: account.esgEnabled,
    lastComment: account.lastComment,
  }
}

export const buildAccountCreateRequest = (account: Account): AccountCreateRequest => ({
  active: true,
  accountName: account.accountName,
  addressLine1: account.addressLine1,
  addressLine2: emptyToNull(account.addressLine2),
  town: emptyToNull(account.town),
  postcode: emptyToNull(account.postcode),
  county: emptyToNull(account.county),
  country: account.country,
  phoneNo: emptyToNull(account.phoneNo),
  accountAlias: emptyToNull(account.accountAlias),
})

export const buildAccountEditRequest = (account: Partial<Account>): AccountEditRequest => {
  const request: AccountEditRequest = {}

  if (account.active !== undefined) request.active = account.active
  if (account.accountName !== undefined) request.accountName = emptyToNull(account.accountName)
  if (account.addressLine1 !== undefined) request.addressLine1 = emptyToNull(account.addressLine1)
  if (account.addressLine2 !== undefined) request.addressLine2 = emptyToNull(account.addressLine2)
  if (account.town !== undefined) request.town = emptyToNull(account.town)
  if (account.postcode !== undefined) request.postcode = emptyToNull(account.postcode)
  if (account.county !== undefined) request.county = emptyToNull(account.county)
  if (account.country !== undefined) request.country = account.country
  if (account.phoneNo !== undefined) request.phoneNo = emptyToNull(account.phoneNo)
  if (account.accountAlias !== undefined) request.accountAlias = emptyToNull(account.accountAlias)
  if (account.exSummaryEnabled !== undefined) request.exSummaryEnabled = account.exSummaryEnabled

  return request
}

export const useAccountsQuery = () =>
  useQuery<Account[]>({
    queryKey: ACCOUNTS_QUERY_KEY,
    queryFn: async ({ signal }) => {
      const response = await backend.get<AccountApiResponse[]>('/accounts', { signal })
      return response.data.map(mapAccountResponse)
    },
    staleTime: Infinity,
  })

// Every account the user can see.
//
// Exists rather than leaving call sites to write `data ?? []` because the fallback's
// identity matters: several consumers list `accounts` in a useEffect or useMemo dependency
// array, and a fresh literal on every render while the query is still loading would re-run
// those on each render. The facade held one module-level empty array for exactly this
// reason, so returning one here is behaviour-preserving rather than an optimisation.
export const useAccounts = () => {
  const { data } = useAccountsQuery()

  return data ?? EMPTY_ACCOUNTS
}

// The active accounts, which is what the default selection resolves to.
export const useActiveAccounts = () => {
  const accounts = useAccounts()

  return useMemo(
    () => accounts.filter((account) => account.active),
    [accounts],
  )
}

// The selected account ids, with the `null` default resolved.
//
// This is the single place the sentinel is decoded, which is what replaced the mount effect
// that used to write every active account id into the store on first load (STATE-1). The
// difference that matters: an account created later is included automatically, because the
// default is derived on read rather than snapshotted at boot.
//
// `[]` is left alone — an explicit empty selection is a real state, not a missing one.
export const useSelectedAccountIds = () => {
  const activeAccounts = useActiveAccounts()
  const selectedAccountIds = useAccountStore((state) => state.selectedAccountIds)

  return useMemo(
    () => selectedAccountIds ?? activeAccounts.map((account) => account.id),
    [selectedAccountIds, activeAccounts],
  )
}

// The selected accounts as entities. The store holds only IDs; this is the single place
// those IDs are joined back to the query that owns the entities.
export const useSelectedAccounts = () => {
  const accounts = useAccounts()
  const selectedAccountIds = useSelectedAccountIds()

  return useMemo(
    () => accounts.filter((account) => selectedAccountIds.includes(account.id)),
    [accounts, selectedAccountIds],
  )
}

const showAccountMutationError = (error: unknown) =>
  notifyApiError(error)

export const useCreateAccountMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (account: Account) =>
      backend.post('/accounts', buildAccountCreateRequest(account)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNTS_QUERY_KEY })
      notifySuccess('Account created successfully')
    },
    onError: showAccountMutationError,
  })
}

export const useUpdateAccountMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      account,
      update,
    }: {
      account: Account
      update: Partial<Account>
    }) => {
      const accountUpdate = { ...update, id: update.id ?? account.id }
      await backend.patch(`/accounts/${account.id}`, buildAccountEditRequest(accountUpdate))
      return accountUpdate
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNTS_QUERY_KEY })
      notifySuccess('Account updated successfully')
    },
    onError: showAccountMutationError,
  })
}

export const useDeleteAccountMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (account: Account) => {
      await backend.delete(`/accounts/${account.id}`)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNTS_QUERY_KEY })
    },
    onError: showAccountMutationError,
  })
}
